import { AttachmentItem } from "../../../models/shared/AttachmentList";
import AttachmentDetails = Office.AttachmentDetails;

export const pathSeparator: string = "\\";

const EXCLUDED_FILE_PATTERNS = [
    // # npm
    "^npm-debug\\.log$",
    "^\\..*\\.swp$",

    // # macOS
    "^\\.DS_Store$",
    "^\\.AppleDouble$",
    "^\\.LSOverride$",
    "^Icon\\r$",
    "^\\._.*",
    "^\\.Spotlight-V100(?:$|\\/)",
    "\\.Trashes",
    "^__MACOSX$",

    // # Linux
    "~$",

    // # Windows
    "^Thumbs\\.db$",
    "^ehthumbs\\.db$",
    "^[Dd]esktop\\.ini$",
    "@eaDir$",
];

/** Filter out OS/system junk files from a list of paths. */
export function filterExcludedFiles<T extends { path: string }>(files: T[]): T[] {
    const isExcludedFile = (file: { path: string }): boolean =>
        EXCLUDED_FILE_PATTERNS.some((pattern) => new RegExp(pattern).test(file.path));

    return files.filter((file) => !isExcludedFile(file));
}

/** Normalize file paths from drag-and-drop (forward slashes → backslash, strip leading slash). */
export function convertFilePathToCorrectFormat(filePathInWrongFormat: string): string {
    return filePathInWrongFormat.startsWith("/")
        ? filePathInWrongFormat.slice(1).replace(/\//g, pathSeparator)
        : filePathInWrongFormat.replace(/\//g, pathSeparator);
}

/** Check if total file count exceeds the 25-file limit. */
export function isTooManyFiles(fileNumber: number): boolean {
    return fileNumber > 25;
}

/** Check if a filename has an extension (e.g. ".pdf"). */
export function hasFileExtension(fileName: string): boolean {
    return /\.[^.]+$/.test(fileName);
}

/** Check if any of the new paths duplicate existing ones (by full path or parent folder path). */
export function isAttachmentDuplicated(paths: { path: string }[], existingPaths: string[]): boolean {
    if (paths.some((x) => existingPaths.includes(x.path.toLowerCase()))) {
        return true;
    }
    const pathsWithoutFilenames = paths.map((x) => getPathWithoutFilename(x.path));
    const existingPathsWithoutFilenames = existingPaths.map((x) => getPathWithoutFilename(x));
    if (pathsWithoutFilenames.some((x) => existingPathsWithoutFilenames.includes(x.toLowerCase()))) {
        return true;
    }
    return false;
}

/** Check if any path contains a folder separator (i.e. a folder was added). */
export function isFolderAdded(paths: { path: string }[]): boolean {
    return paths.some((file) => file.path.includes("\\"));
}

/** Strip the filename from a path, returning only the folder portion. */
export function getPathWithoutFilename(path: string): string {
    const lastPathSeparatorIndex = path.lastIndexOf(pathSeparator);
    return lastPathSeparatorIndex === -1 ? path : path.slice(0, lastPathSeparatorIndex);
}

/** Build the set of existing lowercased paths from current + other-page attachments. */
export function buildExistingPaths(attachments: AttachmentItem[], otherPageAttachments?: AttachmentItem[]): string[] {
    const allAttachments = [...attachments, ...(otherPageAttachments ?? [])];
    return allAttachments
        .map((x) => x.id.toLowerCase())
        .concat(
            allAttachments
                .filter((att: AttachmentItem): att is AttachmentDetails => !!(att as AttachmentDetails).attachmentType)
                .map((x) => x.name.toLowerCase())
        );
}

/**
 * Lowercased paths at which project-folder attachments land in the attachment panel,
 * in the same shape as a locally dropped path ("file.txt" or "FolderA\\file.txt").
 * Project attachments carry an NRN in `id`, so `buildExistingPaths` cannot be used for them.
 */
export function buildProjectAttachmentPaths(projectAttachments?: AttachmentItem[]): string[] {
    return (projectAttachments ?? []).map((item) => {
        const withPath = item as {
            displayPath?: string;
            addToParentFolder?: boolean;
            projectFolderRelativePath?: string;
        };
        const landingPath =
            withPath.addToParentFolder === false
                ? `${withPath.projectFolderRelativePath ?? ""}${pathSeparator}${item.name}`
                : withPath.displayPath ?? item.name;
        return landingPath.toLowerCase();
    });
}

/** Get reviewer attachments not already present in the current list. */
export function getMissingAttachments(
    currentAttachments: AttachmentItem[],
    otherPageAttachments?: AttachmentItem[]
): AttachmentItem[] {
    const currentIds = currentAttachments.map((item) => item.id);
    return otherPageAttachments?.filter((item) => !currentIds.includes(item.id)) ?? [];
}
